import React, { ReactNode, useId, useState } from "react";
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import { Panel, PanelResizeHandle } from "react-resizable-panels";

interface TicketSectionPanelProps {
  children: ReactNode;
  panelId: string;
  order: number;
  label: string;
  divider?: boolean;
}

export function TicketSectionPanel({ children, panelId, order, label, divider }: TicketSectionPanelProps) {
  return (
    <>
      {divider && (
        <Box
          component={PanelResizeHandle}
          aria-label={`Resize ${label}`}
          sx={{
            height: 8,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#F8FAFC",
            cursor: "row-resize",
            "&:hover, &:focus-visible, &[data-resize-handle-state='drag']": { backgroundColor: "#EDE9FE" },
          }}
        >
          <Box sx={{ width: 32, height: 3, borderRadius: 2, backgroundColor: "#CBD5E1" }} />
        </Box>
      )}
      <Panel id={panelId} order={order} minSize={20}>
        {children}
      </Panel>
    </>
  );
}

interface TicketDetailSectionProps {
  title: string;
  label?: string;
  scrollLabel?: string;
  actions?: ReactNode;
  children: ReactNode;
  panelId?: string;
  order?: number;
  divider?: boolean;
  /** Padding inside the bordered body. Ticket properties uses a tighter inset so values keep the width. */
  paperPadding?: number;
}

export default function TicketDetailSection({
  title, label = title, scrollLabel = `${label} content`, actions, children, panelId, order = 1, divider,
  paperPadding = 1.5,
}: TicketDetailSectionProps) {
  const [expanded, setExpanded] = useState(false);
  const titleId = useId();
  const section = (
    <Box component="section" aria-label={label} sx={{
      height: "100%", minHeight: 0, minWidth: 0, boxSizing: "border-box",
      display: "flex", flexDirection: "column", p: 2, backgroundColor: "#FFFFFF",
    }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mb: 1.5, flexShrink: 0 }}>
        <Typography component="h2" sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 700, fontSize: 14, color: "#172033", flex: 1 }}>
          {title}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          {actions}
          <Tooltip title={`Expand ${label}`}>
            <IconButton size="small" aria-label={`Expand ${label}`} onClick={() => setExpanded(true)}>
              <OpenInFullIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Paper elevation={0} tabIndex={0} aria-label={scrollLabel} sx={{
        flex: "1 1 0%", minHeight: 0, minWidth: 0, p: paperPadding, overflowY: "auto", overflowX: "hidden",
        border: "1px solid #E8ECF2", borderRadius: "6px",
      }}>
        {children}
      </Paper>
      <Dialog
        open={expanded}
        onClose={() => setExpanded(false)}
        fullWidth
        maxWidth="lg"
        aria-labelledby={titleId}
        PaperProps={{ sx: { height: "85vh", maxHeight: "calc(100dvh - 32px)", m: 2, width: "calc(100% - 32px)" } }}
      >
        <DialogTitle component="div" id={`${titleId}-header`} sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, borderBottom: "1px solid #E8ECF2" }}>
          <Box component="h2" id={titleId} sx={{ m: 0, flex: 1, fontSize: 18, fontWeight: 600 }}>{title}</Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {actions}
            <IconButton aria-label={`Close ${label}`} onClick={() => setExpanded(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ minHeight: 0, overflowY: "auto", overflowX: "hidden", p: 2 }}>
          {children}
        </DialogContent>
      </Dialog>
    </Box>
  );
  return panelId ? (
    <TicketSectionPanel panelId={panelId} order={order} label={label} divider={divider}>{section}</TicketSectionPanel>
  ) : section;
}
